import { get, set } from 'lodash';
import {
  ComponentType,
  type IFormilySchema,
  type IFormFieldSchema,
} from '../../types';
import { ReferenceType } from '../../../types';
import type { IFormReferenceSelectProps } from '../reference-select/helper';
import { transformToReferenceValue } from '../reference-select/utils';
import { findFormComponent } from '../../utils/findFormComponent';
import { findByIdInData } from '../../utils/enrichInternalReferenceData';
import { parseSubSchema } from '../form-recursion-item/utils';
import type { IChildCollection } from '../form-recursion-item/utils';

/**
 * Extracts the form properties from a childSchema.
 * Handles nested Form components by looking for properties within childSchemaForm.
 */
const getFormProperties = (
  childSchema?: IFormilySchema
): Record<string, IFormilySchema> => {
  if (!childSchema?.properties) return {};

  // Check if there's a nested form structure (childSchemaForm)
  const formSchema = childSchema.properties.childSchemaForm;
  if (formSchema?.properties) {
    return formSchema.properties;
  }

  return childSchema.properties;
};

/**
 * Builds initial values from raw data based on the childSchema structure.
 * Transforms FormReferenceSelect fields to IReferenceValue format.
 * Handles nested FormArrayTable components recursively.
 * Depricated
 */
export const buildInitialValuesFromSchema = (
  initialData: any[],
  childSchema?: IFormilySchema
): any[] => {
  if (!initialData || !Array.isArray(initialData) || initialData.length === 0) {
    return [];
  }

  if (!childSchema) {
    return initialData;
  }

  const formProperties = getFormProperties(childSchema);

  return initialData.map(row => {
    const transformedRow: any = { id: row.id };

    // Iterate through schema properties to determine how to transform each field
    Object.keys(formProperties).forEach(fieldName => {
      const fieldSchema = formProperties[fieldName];
      const fieldValue = row[fieldName];

      // Skip if field doesn't exist in row data
      if (fieldValue === undefined || fieldValue === null) return;

      // Handle nested FormArrayTable (recursive)
      if (fieldSchema['x-component'] === ComponentType.FormArrayTable) {
        const nestedChildSchema = (
          fieldSchema as unknown as IFormFieldSchema<any>
        )['x-component-props']?.childSchema;
        if (Array.isArray(fieldValue) && nestedChildSchema) {
          transformedRow[fieldName] = buildInitialValuesFromSchema(
            fieldValue,
            nestedChildSchema
          );
        } else {
          transformedRow[fieldName] = fieldValue;
        }
      }
      // Transform FormReferenceSelect / FormReferenceTreeSelect fields to IReferenceValue format
      else if (
        fieldSchema['x-component'] === ComponentType.FormReferenceSelect ||
        fieldSchema['x-component'] === ComponentType.FormReferenceTreeSelect ||
        fieldSchema['x-component'] ===
          ComponentType.FormReferenceTreeSelectAsync
      ) {
        if (Array.isArray(fieldValue)) {
          // Handle multiple mode
          transformedRow[fieldName] = fieldValue.map(v =>
            transformToReferenceValue(
              v,
              (
                fieldSchema as unknown as IFormFieldSchema<IFormReferenceSelectProps>
              )['x-component-props']?.transformer
            )
          );
        } else {
          // Handle single mode
          transformedRow[fieldName] = transformToReferenceValue(
            fieldValue,
            (
              fieldSchema as unknown as IFormFieldSchema<IFormReferenceSelectProps>
            )['x-component-props']?.transformer
          );
        }
      } else {
        // Keep other fields as is
        transformedRow[fieldName] = fieldValue;
      }
    });

    return transformedRow;
  });
};

function collectTransformedFields(
  properties: Record<string, IFormilySchema>,
  row: any,
  transformedRow: any,
  rootData?: any
): void {
  for (const [fieldName, fieldSchema] of Object.entries(properties)) {
    if (fieldSchema.type === 'void' && fieldSchema.properties) {
      collectTransformedFields(
        fieldSchema.properties,
        row,
        transformedRow,
        rootData
      );
      continue;
    }

    const fieldValue = get(row, fieldName);

    if (fieldValue === undefined || fieldValue === null) continue;

    if (fieldSchema['x-component'] === ComponentType.FormArrayTable) {
      const nestedChildSchema = (
        fieldSchema as unknown as IFormFieldSchema<any>
      )['x-component-props']?.childSchema;
      const value =
        Array.isArray(fieldValue) && nestedChildSchema
          ? buildInitialValuesFromSchemaV2(
              fieldValue,
              nestedChildSchema,
              rootData
            )
          : fieldValue;
      set(transformedRow, fieldName, value);
    } else if (fieldSchema['x-component'] === ComponentType.FormRecursionItem) {
      const nestedChildSchema = (
        fieldSchema as unknown as IFormFieldSchema<any>
      )['x-component-props']?.childSchema;
      if (!Array.isArray(fieldValue) || !nestedChildSchema) {
        set(transformedRow, fieldName, fieldValue);
      } else {
        const parsedChildSchema =
          nestedChildSchema?.properties?.childSchemaForm &&
          typeof (nestedChildSchema.properties.childSchemaForm as any) ===
            'string'
            ? {
                ...nestedChildSchema,
                properties: {
                  ...nestedChildSchema.properties,
                  childSchemaForm: JSON.parse(
                    nestedChildSchema.properties
                      .childSchemaForm as unknown as string
                  ),
                },
              }
            : nestedChildSchema;
        set(
          transformedRow,
          fieldName,
          applyChildCollectionValues(
            buildInitialValuesFromSchemaV2(
              fieldValue,
              parsedChildSchema,
              rootData
            ),
            fieldValue,
            (fieldSchema as unknown as IFormFieldSchema<any>)[
              'x-component-props'
            ]?.childCollections,
            rootData
          )
        );
      }
    } else if (
      fieldSchema['x-component'] === ComponentType.FormReferenceSelect ||
      fieldSchema['x-component'] === ComponentType.FormReferenceTreeSelect ||
      fieldSchema['x-component'] === ComponentType.FormReferenceTreeSelectAsync
    ) {
      const props = (
        fieldSchema as unknown as IFormFieldSchema<IFormReferenceSelectProps>
      )['x-component-props'];
      const referenceType = props?.referenceType;
      const transformer = props?.transformer;

      if (referenceType === ReferenceType.INTERNAL_REFERENCE && rootData) {
        const resolve = (v: any) => {
          const found = findByIdInData(rootData, v?.id);
          return found
            ? transformToReferenceValue(found, transformer, referenceType)
            : v;
        };
        const value = Array.isArray(fieldValue)
          ? fieldValue.map(resolve)
          : resolve(fieldValue);
        set(transformedRow, fieldName, value);
      } else {
        const value = Array.isArray(fieldValue)
          ? fieldValue.map(v =>
              transformToReferenceValue(v, transformer, referenceType)
            )
          : transformToReferenceValue(fieldValue, transformer, referenceType);
        set(transformedRow, fieldName, value);
      }
    } else {
      set(transformedRow, fieldName, fieldValue);
    }
  }
}

/**
 * Re-attaches a recursion node's child-collection rows (`manufacturedItems`,
 * `devices`, …). They are not part of the node form, so the schema-driven
 * rebuild drops them; each one is transformed with its own row schema so
 * references keep their display values.
 */
export const applyChildCollectionValues = (
  transformed: any[],
  source: any[],
  collections?: IChildCollection[],
  rootData?: any
): any[] => {
  if (!collections?.length) return transformed;
  return transformed.map((row, index) =>
    collections.reduce((acc, collection) => {
      const rows = source?.[index]?.[collection.field];
      if (!Array.isArray(rows)) return acc;
      return {
        ...acc,
        [collection.field]: buildInitialValuesFromSchemaV2(
          rows,
          parseSubSchema(collection.formSchema),
          rootData
        ),
      };
    }, row)
  );
};

export const buildInitialValuesFromSchemaV2 = (
  initialData: any[],
  childSchema?: IFormilySchema,
  rootData?: any
): any[] => {
  if (!initialData?.length || !Array.isArray(initialData)) return [];
  if (!childSchema) return initialData;

  const formComponent = findFormComponent(childSchema);
  if (!formComponent?.properties) return initialData;

  return initialData.map(row => {
    // Row markers aren't schema fields; dropping them would resurrect deleted
    // rows and make unsaved ones look persisted.
    const transformedRow: any = {
      id: row.id,
      ...(row.toBeDeleted && { toBeDeleted: true }),
      ...(row._formLocal && { _formLocal: true }),
    };
    collectTransformedFields(
      formComponent.properties!,
      row,
      transformedRow,
      rootData
    );
    return transformedRow;
  });
};
